import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import clsx from "clsx";
import type { Board } from "../../types";
import { useStore } from "../../stores/store";
import { BOARD_PALETTE, getBoardColor, getBoardInitials } from "../../lib/boardVisuals";
import { ApiError } from "../../lib/api";
import { BOARD_ICON_KEYS, BOARD_ICON_MAP } from "./boardIcons";

interface Props {
  open: boolean;
  mode: "create" | "rename" | "visuals";
  board?: Board | null;
  onClose: () => void;
}

export function BoardEditorModal({ open, mode, board, onClose }: Props) {
  return (
    <AnimatePresence>
      {open && <EditorBody mode={mode} board={board ?? null} onClose={onClose} />}
    </AnimatePresence>
  );
}

function EditorBody({
  mode,
  board,
  onClose,
}: {
  mode: "create" | "rename" | "visuals";
  board: Board | null;
  onClose: () => void;
}) {
  const boards = useStore((s) => s.boards);
  const createBoard = useStore((s) => s.createBoard);
  const renameBoard = useStore((s) => s.renameBoard);
  const updateBoardVisuals = useStore((s) => s.updateBoardVisuals);

  const initialTitle = mode === "create" ? "" : board?.title ?? "";
  const initialIcon = mode === "create" ? null : board?.icon ?? null;
  const initialColor = mode === "create" ? null : board?.color ?? null;

  const [title, setTitle] = useState(initialTitle);
  const [icon, setIcon] = useState<string | null>(initialIcon);
  const [color, setColor] = useState<string | null>(initialColor);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const showTitle = mode === "create" || mode === "rename";

  useEffect(() => {
    if (showTitle) {
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 40);
      return () => clearTimeout(t);
    }
  }, [showTitle]);

  const heading = useMemo(() => {
    if (mode === "create") return "Novo quadro";
    if (mode === "rename") return "Renomear quadro";
    return "Trocar ícone, cor";
  }, [mode]);

  const previewTitle = showTitle ? title || "Quadro" : board?.title ?? "Quadro";
  const previewColor = getBoardColor(previewTitle, color);
  const PreviewIcon = icon ? BOARD_ICON_MAP[icon as keyof typeof BOARD_ICON_MAP] : null;

  function validate(): string | null {
    if (showTitle) {
      const t = title.trim();
      if (!t) return "Dê um nome ao quadro.";
      if (t.length > 40) return "Máximo de 40 caracteres.";
      const dup = boards.find(
        (b) =>
          !b.archived &&
          b.title.trim().toLowerCase() === t.toLowerCase() &&
          (mode === "create" || b.id !== board?.id),
      );
      if (dup) return "Já existe um quadro com esse nome.";
    }
    return null;
  }

  async function confirm() {
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      if (mode === "create") {
        await createBoard(title.trim(), { icon, color });
      } else if (mode === "rename") {
        if (!board) return;
        const newTitle = title.trim();
        if (newTitle !== board.title) {
          await renameBoard(board.id, newTitle);
        }
        if (icon !== board.icon || color !== board.color) {
          await updateBoardVisuals(board.id, { icon, color });
        }
      } else if (mode === "visuals") {
        if (!board) return;
        await updateBoardVisuals(board.id, { icon, color });
      }
      onClose();
    } catch (e) {
      if (e instanceof ApiError) setErr(e.message);
      else setErr("Falha ao salvar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "Enter") {
      e.preventDefault();
      confirm();
    }
  }

  useEffect(() => {
    function onWindowKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onWindowKey);
    return () => window.removeEventListener("keydown", onWindowKey);
  }, [onClose]);

  return (
    <motion.div
      key="board-editor-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
    >
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.95, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.97, y: 6, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 300 }}
        className="glass relative w-[480px] max-w-[92vw] overflow-hidden rounded-2xl p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />

        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={clsx(
                "flex h-9 w-9 items-center justify-center rounded-lg text-sm font-semibold",
                previewColor.bg,
                previewColor.text,
              )}
            >
              {PreviewIcon ? <PreviewIcon className="h-4 w-4" /> : getBoardInitials(previewTitle)}
            </div>
            <h3 className="text-base font-semibold text-slate-100">{heading}</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {showTitle && (
          <div className="mb-4">
            <label className="mb-1 block text-xs text-slate-400">Nome</label>
            <input
              ref={inputRef}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (err) setErr(null);
              }}
              onKeyDown={onKey}
              maxLength={40}
              placeholder="Ex: Tese de Mestrado"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-violet-500/60 focus:outline-none"
            />
            {err && <p className="mt-2 text-xs text-rose-300">{err}</p>}
          </div>
        )}

        <div className="mb-4">
          <label className="mb-2 block text-xs text-slate-400">Ícone</label>
          <div className="grid grid-cols-4 gap-2">
            <button
              type="button"
              onClick={() => setIcon(null)}
              className={clsx(
                "flex h-10 items-center justify-center rounded-lg bg-white/5 text-xs text-slate-300 transition hover:bg-white/10",
                icon === null && "ring-2 ring-violet-400",
              )}
              title="Sem ícone (usar iniciais)"
            >
              <span className="font-semibold">Aa</span>
            </button>
            {BOARD_ICON_KEYS.map((key) => {
              const Icon = BOARD_ICON_MAP[key];
              const selected = icon === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  className={clsx(
                    "flex h-10 items-center justify-center rounded-lg bg-white/5 text-slate-200 transition hover:bg-white/10",
                    selected && "ring-2 ring-violet-400",
                  )}
                  title={key}
                >
                  <Icon className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-5">
          <label className="mb-2 block text-xs text-slate-400">Cor</label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setColor(null)}
              className={clsx(
                "flex h-9 items-center gap-1 rounded-full bg-white/5 px-3 text-xs text-slate-200 transition hover:bg-white/10",
                color === null && "ring-2 ring-white",
              )}
              title="Cor automática (pelo nome)"
            >
              <Sparkles className="h-3 w-3" />
              Auto
            </button>
            {BOARD_PALETTE.map((c) => {
              const selected = color === c.name;
              return (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => setColor(c.name)}
                  className={clsx(
                    "h-7 w-7 rounded-full transition",
                    c.bg,
                    selected && "ring-2 ring-white",
                  )}
                  title={c.name}
                />
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={submitting}
            className="rounded-lg bg-violet-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-violet-400 disabled:opacity-60"
          >
            {submitting ? "Salvando..." : mode === "create" ? "Criar" : "Salvar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
